import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  Provider,
  provideZoneChangeDetection,
} from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  PreloadAllModules,
  provideRouter,
  Routes,
  withComponentInputBinding,
  withPreloading,
} from '@angular/router';
import { provideToastr } from 'ngx-toastr';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import {
  CREDIT_MILES_API,
  CREDIT_POINTS_API,
  FACTS_API,
  HOTELS_API,
  LOADING_SERVICE,
  LOCATION_SELECTOR_ENABLED,
  PROFILE_API,
  PROFILE_DETAILS_API,
  PROFILE_LOYALTY_API,
  REDEEM_POINTS_API,
  TIME_ZONE,
  VOUCHER_API,
} from '@ireckonu/shared-ui';

import { APP_ENV, AppEnv, resolveAppEnv } from '../data-access/app-config';
import {
  ConfigurationQueryService,
  FactsService,
  HotelsService,
  LoyaltyDialogsService,
  ProfileDetailsService,
  ProfilesService,
  TimeZoneService,
  VoucherService,
} from '../data-access/service';
import { authInterceptor } from './auth.interceptor';
import { httpErrorInterceptor } from './http-error.interceptor';
import { loggingInterceptor } from './logging.interceptor';
import { NoOpLoadingService } from './no-op-loading.service';
import { iruTranslocoConfig } from './transloco.config';

export const getAppProviders = (
  value: AppEnv,
  appRoutes: Routes,
): (Provider | EnvironmentProviders)[] => [
  {
    provide: APP_ENV,
    // Applies any persisted reviewer override on reload (see resolveAppEnv).
    useFactory: () => resolveAppEnv(value),
  },
  {
    provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
    useValue: { appearance: 'outline' },
  },
  { provide: MAT_DATE_LOCALE, useValue: 'en-NL' },
  {
    provide: MAT_DIALOG_DEFAULT_OPTIONS,
    useValue: { width: '500px', delayFocusTrap: true },
  },
  { provide: REDEEM_POINTS_API, useClass: LoyaltyDialogsService },
  { provide: CREDIT_POINTS_API, useClass: LoyaltyDialogsService },
  { provide: CREDIT_MILES_API, useClass: LoyaltyDialogsService },
  { provide: PROFILE_LOYALTY_API, useClass: LoyaltyDialogsService },
  { provide: FACTS_API, useClass: FactsService },
  { provide: HOTELS_API, useClass: HotelsService },
  { provide: LOADING_SERVICE, useClass: NoOpLoadingService },
  {
    provide: LOCATION_SELECTOR_ENABLED,
    useFactory: (): (() => boolean) => {
      const configService = inject(ConfigurationQueryService);
      return (): boolean => configService.features()?.venues ?? false;
    },
  },
  { provide: PROFILE_API, useClass: ProfilesService },
  { provide: PROFILE_DETAILS_API, useExisting: ProfileDetailsService },
  { provide: TIME_ZONE, useClass: TimeZoneService },
  { provide: VOUCHER_API, useExisting: VoucherService },
  provideTransloco({
    config: iruTranslocoConfig,
  }),
  provideTranslocoMessageformat(),
  importProvidersFrom([MatSnackBarModule]),
  provideRouter(
    appRoutes,
    withPreloading(PreloadAllModules),
    withComponentInputBinding(),
  ),
  provideZoneChangeDetection({ eventCoalescing: true }),
  provideHttpClient(
    withInterceptors([
      loggingInterceptor,
      authInterceptor,
      httpErrorInterceptor,
    ]),
  ),
  // TODO: Remove eslint-disable when Angular provides a non-deprecated alternative for Material animations
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  provideAnimationsAsync(),
  provideToastr(),
];
